import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import ActionItems from "../../components/ActionItems/ActionItems";
import ActionItem from "../../components/ActionItems/ActionItem";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { initialSort } from "../../components/ActionItems/actionItemTableConfig";
import ProgressSpinner from "../../components/Common/ProgressSpinner";
import { ActionItemType } from "../../utils/types";
import ClientAPIMeetings from "../../api/clientAPI";

const ClientPendingActionItem: React.FC<{ clientDomain: string, loggedInEmail?: string, selectedUserEmail?: string }> = ({ 
  clientDomain, loggedInEmail, selectedUserEmail}) => {
  const theme = useTheme();
  const isLargeUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { showSnackbar } = useDisplaySnackbar();
  const [attendees, setAttendees] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [actionItems, setActionItems] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [selectedActionItem, setSelectedActionItem] = useState<any | null>(null);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState<string>("");

  const [filters, setFilters] = useState<{
    date: { start_time: string; end_time: string } | undefined;
    status: undefined | string;
  }>({
    date: undefined,
    status: "todo,in_progress",
  });

  const [sort, setSort] = useState(initialSort);
  const [actionItemType, setActionItemType] = useState(ActionItemType.All);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const today = new Date();
  const endOfToday = new Date(today);
  endOfToday.setHours(today.getHours(), today.getMinutes(), 0, 0);
  const startOfThreeMonthsAgo = new Date(today);
  startOfThreeMonthsAgo.setMonth(today.getMonth() - 3);
  startOfThreeMonthsAgo.setHours(0, 0, 0, 0);
  const startTime = startOfThreeMonthsAgo.toISOString();
  const endTime = endOfToday.toISOString();

  const {
    isLoading: isTeamActionsLoading,
    isFetching: isTeamActionsFetching,
    isSuccess: isTeamActionsDataSuccess,
    isError: isTeamActionsDataError,
    error: teamActionsDataError,
    data: myClientActionsData,
  } = ClientAPIMeetings.useGetPendingActionItem(
    {

      start_time: startTime,
      end_time: endTime,
      client_domain: clientDomain,
      page: pageNumber,
      per_page: pageSize,
      search: search,
      status: filters.status || "",
      sort: sort,
      types: actionItemType,
      include_me: loggedInEmail && selectedUserEmail ? loggedInEmail === selectedUserEmail : true,
      ...(loggedInEmail !== selectedUserEmail && { employee_email_ids: selectedUserEmail }),
    },
    true
  );
  useEffect(() => {
    const startTimeEndTime =
      localStorage.getItem("analyticsStartEndTime") &&
      JSON.parse(localStorage.getItem("analyticsStartEndTime") as string);

    startTimeEndTime &&
      setFilters({
        date: startTimeEndTime,
        status: undefined,
      });

    localStorage.removeItem("analyticsStartEndTime");
  }, []);



  useEffect(() => {
    setShowSpinner(isTeamActionsLoading || isTeamActionsFetching);
    if (isTeamActionsDataSuccess) {
      const teamActionItemList = myClientActionsData?.data?.data?.actions;
      // todo: use common state, reset at correct place
      setActionItems(teamActionItemList);
      setTotalRecord(myClientActionsData?.data?.total_records);
    }
    if (isTeamActionsDataError) {
      showSnackbar("error", teamActionsDataError);
    }
  }, [
    isTeamActionsLoading,
    isTeamActionsFetching,
    isTeamActionsDataSuccess,
    isTeamActionsDataError,
  ]);

  const onPageChange = (value: number) => {
    setPageNumber(value);
  };

  const onSearch = (value: string) => {
    setSearch(value);
    setPageNumber(1);
  };

  const onFilter = (value: {
    date: { start_time: string; end_time: string } | undefined;
    status: undefined | string;
  }) => {
    setFilters(value);
    setPageNumber(1);
  };

  const onTypeChange = (value: ActionItemType) => {
    setActionItemType(value);
    setPageNumber(1);
  };

  const onSort = (value: { sort_field: string; sort_order: string }) => {
    setSort(value as { sort_field: string; sort_order: "asc" | "desc" });
  };

  const handleActionItemSelection = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem(actionItem);
    setAttendees([actionItem?.assignee]);
  };

  const handleActionItemPreview = (actionItem: any) => {
    setOpenDialog(true);
    setSelectedActionItem(actionItem);
    setAttendees([actionItem?.assignee]);
    setIsPreview(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedActionItem(null);
    setIsPreview(false);
  };

  return (
    <>
      <Box
        id="actionItemsParent"
        className="flex flex-col justify-start items-start gap-6"
        style={{ marginTop: "-10px" }}
      >
        <Box className="actionItemsContent w-full">
          <Box sx={{ marginTop: "10px" }}>
            <ActionItems
              type={"TEAM_ACTION_ITEMS"}
              isLoading={isTeamActionsLoading}
              actionItems={actionItems}
              totalRecord={totalRecord}
              handleActionItemSelection={handleActionItemSelection}
              handleActionItemPreview={handleActionItemPreview}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onSearch={onSearch}
              onFilter={onFilter}
              onSort={onSort}
              actionItemType={actionItemType}
              onTypeChange={onTypeChange}
            />
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleClose}
        fullWidth
        maxWidth={isLargeUp ? "md" : "sm"}
        sx={{
          "& .MuiDialogTitle-root": {
            padding: "16px 22px",
          },
          "& .MuiDialogContent-root": {
            padding: "16px 22px",
            paddingTop: "16px",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h3">
            {selectedActionItem ? "Update Action" : "Add Action"}
          </Typography>
          <Typography variant="h5" sx={{ color: "#717171" ,marginBottom:"-15px"}}>
            Fields marked with <span style={{ color: "#FF2E00" }}>*</span> are
            mandatory to be filled.
          </Typography>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ActionItem
            actionItemType={"TEAM_ACTION_ITEMS"}
            attendees={attendees}
            selectedActionItem={selectedActionItem}
            handleClose={handleClose}
            isPreview={isPreview}
          />
        </DialogContent>
      </Dialog>
      <ProgressSpinner showSpinner={showSpinner} />
    </>
  );
};


export default ClientPendingActionItem;